import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { track, useTrackScreen } from '@/lib/analytics';
import { View, Text, ScrollView } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { ScreenFrame } from '@/components/ScreenFrame';
import { useRouter } from 'expo-router';
import {
  Compass,
  MapPin,
  Sparkles,
  Plus,
  LayoutGrid,
  LayoutList,
  List,
  Eye,
  EyeOff,
} from 'lucide-react-native';
import Animated, { FadeInUp, FadeIn } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useHasHydrated } from '@/lib/state/inverio-store';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { useShallow } from 'zustand/react/shallow';
import { usePhoneShell } from '@/lib/useShell';
import { useResponsive } from '@/lib/useResponsive';
import { AppLayout } from '@/components/AppLayout';
import AILauncherFAB from '@/components/AILauncherFAB';
import { SearchBar, FilterChips, FilterPanel, SortDropdown } from '@/components/SearchFilters';
import { useListLayout, ListLayoutType } from '@/components/ListLayout';
import { SectionDivider } from '@/components/SectionDivider';
import { SectionHeader, Card } from '@/components/ui';
import { DealflowSkeletonCard, StatsSkeletonCard } from '@/components/Skeleton';
import { hapticLight, hapticMedium, hapticSuccess } from '@/lib/haptics';
import {
  useDealflowSearch,
  useComparison,
  useBulkActions,
  useSavedItems,
  calculateMatchScore,
} from '@/lib/state/marketplace';
import { useMyDataRoomAccessRequests } from '@/lib/supabase/api';
import { requestStateFor } from '@/lib/dd/dd-entry';
import { useInvestorProfile } from '@/lib/supabase';
import { useRaisingStartups, useAddRaisingStartupToDealflow } from '@/lib/supabase/investor-queries';
import { selectDDReviews } from '@/lib/dd/dd-entry';
import { buildDealStatusConfig, buildRaisingStatusConfig } from '@/components/investor/deal-status-badges';
import { useRequestDataRoomAccess } from '@/lib/dealflow/deal-actions';
import { useChangeStage } from '@/lib/dealflow/use-change-stage';
import { mode } from '@/lib/config/app-mode';
import { useAlert } from '@/components/AlertDialog';
import { appearsOn, isRaisingEntryStage, type DealStage } from '@/lib/dealflow/stage';
import { handleError } from '@/lib/errors';
import { useAiConsentGate } from '@/lib/consent/ai-consent';

import { DealflowCard } from '@/components/investor/DealflowCard';
import { SealedDealSlot } from '@/components/investor/SealedDealSlot';
import { useSealedDeals } from '@/lib/dealflow/sealed-deals';
import { PitchDeckAnalysisModal } from '@/components/investor/PitchDeckAnalysisModal';
import { DealflowEmptyState } from '@/components/investor/DealflowEmptyState';
import { ComparisonBar, BulkActionsBar } from '@/components/investor/DealflowActionBars';
import { PlatformDealflowLocked } from '@/components/investor/PlatformDealflowLocked';
import { useCurrentTier } from '@/lib/state/subscription-store';
import { investorCanSeePlatformDealflow } from '@/lib/subscription/dealflow-gate';
import type { InvestorTier } from '@/lib/subscription/types';
import { DeckAnalysisCards } from '@/components/investor/DeckAnalysisCards';
import { useDeckAnalyses } from '@/lib/investor/use-deck-analyses';
import { openPlans } from '@/lib/commerce/navigation';

const LAYOUT_OPTIONS = [
  { value: 'list' as ListLayoutType, icon: LayoutList, label: 'List' },
  { value: 'grid' as ListLayoutType, icon: LayoutGrid, label: 'Grid' },
  { value: 'compact' as ListLayoutType, icon: List, label: 'Compact' },
];

const noop = () => {};

export default function DealflowScreen() {
  useTrackScreen('Dealflow');
  const dealflowTracked = useRef(false);
  useEffect(() => {
    if (dealflowTracked.current) return;
    dealflowTracked.current = true;
    track('dealflow_viewed');
  }, []);
  const router = useRouter();
  const { isDesktop } = useResponsive();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const hasHydrated = useHasHydrated();

  // D3 entitlement gate: free investors get a teaser/inbox, not the full
  // platform pipeline. `useCurrentTier()` stays optimistic during the async
  // subscription-load window (P0-4), so paying users are never flashed the gate.
  const investorTier = useCurrentTier() as InvestorTier;
  const canSeePlatformDealflow = investorCanSeePlatformDealflow(investorTier);

  const statusConfig = useMemo(() => buildDealStatusConfig(colors), [colors]);
  const raisingStatusConfig = useMemo(() => buildRaisingStatusConfig(colors), [colors]);

  const [showFilters, setShowFilters] = useState(false);
  const [showNewAnalysis, setShowNewAnalysis] = useState(false);
  // Gate the deck-analysis flow on explicit AI-processing consent — checked
  // before the modal opens (`openNewAnalysis`) and again if the modal itself
  // hits a 403 (`handleAnalysisConsentRequired`, a race or a withdrawal from
  // another device). Two AppModals must never stack, so a 403 detected
  // INSIDE PitchDeckAnalysisModal closes it first and reopens the sheet here,
  // at the owning screen.
  const analysisConsentGate = useAiConsentGate(['pitch_deck_analysis']);
  const openNewAnalysis = useCallback(() => {
    analysisConsentGate.runWithConsent(() => setShowNewAnalysis(true));
  }, [analysisConsentGate]);
  const handleAnalysisConsentRequired = useCallback(() => {
    setShowNewAnalysis(false);
    analysisConsentGate.openConsentSheet();
  }, [analysisConsentGate]);
  // Polls only while something is actually in flight (see the hook).
  const { data: deckAnalyses } = useDeckAnalyses();
  const { layout, setLayout } = useListLayout('list');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isMasked, setIsMasked] = useState(true);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Store hooks
  const dealflow = useDealflowStore(useShallow((s) => s.dealflow));
  const commitments = useDealflowStore(useShallow((s) => s.commitments));
  const changeStage = useChangeStage();
  const investorProfile = useProfileStore(useShallow((s) => s.investorProfile));

  // ── Due-diligence entry ──
  // Requesting data-room access is the investor's explicit action: one
  // persisted row, one stage move, one signal both the DD screen and the
  // dashboard read (see lib/dd/dd-entry.ts). Before this, dealflow had no entry
  // point at all — the only way in was a button buried on the startup-detail
  // screen, which is why `data_room_access_requests` was empty in production.
  // Entering DILIGENCE is not this action's to do: the founder's approval does
  // that (respond_to_data_room_request).
  const user = useProfileStore((s) => s.user);
  const { data: investorProfileRow } = useInvestorProfile(
    mode.showSampleData ? undefined : user?.id,
  );
  const { data: myAccessRequests = [] } = useMyDataRoomAccessRequests(
    mode.showSampleData ? undefined : investorProfileRow?.id,
  );
  const { requestDataRoomAccess } = useRequestDataRoomAccess();
  // WHICH deal is in flight, not merely THAT one is: the hook exposes a single
  // mutation, so a bare `isPending` would dim every Request-access button on
  // the screen at once.
  const [requestingDealId, setRequestingDealId] = useState<string | null>(null);
  const { error: showDDError, success: showDDSuccess } = useAlert();

  const ddStateByStartupId = useMemo(
    () =>
      new Map(
        selectDDReviews(dealflow, myAccessRequests).map((r) => [
          r.deal.startup.id,
          r.accessState,
        ]),
      ),
    [dealflow, myAccessRequests],
  );

  /**
   * `open-diligence` — navigation only.
   *
   * It used to also CREATE the access request when the deal was not yet in DD.
   * That branch is gone, and could not fire even before it was removed:
   * `open-diligence` is offered only at stage `diligence` (ACTIONS in
   * stage.ts), and a deal only reaches `diligence` through the founder's
   * approval of a request — so it was always already in `ddStateByStartupId`
   * and always took the first branch. Requesting is `request-access`'s job,
   * below, and lives in one place for both surfaces.
   */
  const handleDDAction = useCallback(
    async (dealId: string) => {
      const deal = dealflow.find((d) => d.id === dealId);
      if (!deal) return;
      const startupId = deal.startup.id;
      track('dd_opened_from_dealflow', { startupId });
      router.push(`/duediligence/${startupId}`);
    },
    [dealflow, router],
  );

  /**
   * `request-access` — ask the founder to open the data room, and move the deal
   * to `awaiting_access`. Both writes live in `useRequestDataRoomAccess` so this
   * screen and startup-detail cannot do half of it each, which is exactly what
   * they used to do.
   */
  const handleRequestAccess = useCallback(
    async (dealId: string) => {
      const deal = dealflow.find((d) => d.id === dealId);
      if (!deal) return;
      const startupId = deal.startup.id;

      // Sample mode has no server to request against; open the review so the
      // demo still walks the flow.
      if (mode.showSampleData) {
        router.push(`/duediligence/${startupId}`);
        return;
      }

      if (!investorProfileRow?.id) {
        showDDError(
          'Profile not ready',
          'Your investor profile is still loading. Try again in a moment.',
        );
        return;
      }

      hapticMedium();
      setRequestingDealId(dealId);
      try {
        await requestDataRoomAccess({
          dealId,
          startupId,
          investorProfileId: investorProfileRow.id,
          investorUserId: user?.id,
        });
        track('dealflow_status_changed', { startupId: dealId, newStatus: 'awaiting_access' });
        hapticSuccess();
        showDDSuccess(
          'Access requested',
          `We've asked ${deal.startup.company.name} to open their data room. Diligence starts once they approve.`,
        );
      } catch (err) {
        // The stage is NOT moved when this throws — the deal stays at
        // `interested` and the button stays available, rather than stranding it
        // at `awaiting_access` with nobody asked.
        const { message, isCancelled } = handleError(err, {
          context: 'Dealflow.handleRequestAccess',
        });
        if (!isCancelled) showDDError("Couldn't request access", message);
      } finally {
        setRequestingDealId(null);
      }
    },
    [
      dealflow,
      investorProfileRow?.id,
      mode.showSampleData,
      requestDataRoomAccess,
      router,
      showDDError,
      showDDSuccess,
    ],
  );

  const getMyCommitment = useCallback((startupId: string) => {
    return commitments.find(
      (c) =>
        c.startupId === startupId &&
        c.investorId === investorProfile?.id &&
        (c.status === 'active' || c.status === 'converted')
    );
  }, [commitments, investorProfile?.id]);

  // Platform feed of actively-raising startups ("Raising Now"). The hook is
  // disabled in demo/sample mode; items already in the pipeline are hidden.
  const { data: raisingFeed = [] } = useRaisingStartups();
  const addRaisingToDealflow = useAddRaisingStartupToDealflow();
  const raisingItems = useMemo(() => {
    const pipelineStartupIds = new Set(dealflow.flatMap((d) => [d.id, d.startup.id]));
    return raisingFeed
      .filter((item) => !pipelineStartupIds.has(item.startup.id))
      .map((item) => ({
        item,
        matchScore: investorProfile
          ? calculateMatchScore(item.startup, investorProfile, item.readinessScore ?? undefined)
          : null,
      }));
  }, [raisingFeed, dealflow, investorProfile]);

  /**
   * Interested/Reject/Monitor on a raising-feed item creates a REAL dealflow
   * row. `handleRaisingStatus` is NOT a transition — the feed item has no
   * dealflow row yet, so there is no current stage to move FROM. Creating one
   * is equivalent to a deal arriving at `screening` and being acted on in the
   * same gesture, so the legal targets are precisely `screening`'s
   * transitions: interested, rejected, monitoring. This is what makes the
   * "Monitor" button on a raising-feed card actually do something instead of
   * silently returning (it was rendered and enabled via `offeredActions`
   * before this guard admitted its target).
   */
  const handleRaisingStatus = useCallback(
    // `_from` is ignored on purpose: the card reports where it thinks the item
    // is (raising items render as `screening`), but there is no row and so no
    // stage to move FROM. What happens here is a CREATE, and its legal entry
    // stages are `screening`'s transitions — which is the check below, not the
    // card's claim.
    (feedItemId: string, _from: DealStage, status: DealStage) => {
      if (!isRaisingEntryStage(status)) return;
      const feedItem = raisingItems.find(({ item }) => item.id === feedItemId)?.item;
      if (!feedItem || !investorProfile?.id) return;
      hapticMedium();
      addRaisingToDealflow.mutate({
        investorProfileId: investorProfile.id,
        feedItem,
        status,
      });
    },
    [raisingItems, investorProfile?.id, addRaisingToDealflow],
  );

  const handleViewRaisingStartup = useCallback((startupId: string) => {
    router.push(`/startup-detail?id=${startupId}`);
  }, [router]);

  // Search and filter hook
  const {
    filters, updateFilter, toggleArrayFilter, resetFilters,
    itemsWithMatch, sortBy, setSortBy, activeFilterCount,
  } = useDealflowSearch();

  // Comparison hook
  const {
    comparisonIds, comparisonCount, addToComparison,
    removeFromComparison, isInComparison, clearComparison, canAddMore,
  } = useComparison(4);

  // Bulk actions hook
  const {
    selectedCount, isSelectionMode, toggleSelection, selectAll,
    exitSelectionMode, isSelected, enterSelectionMode, selectedIds,
  } = useBulkActions(itemsWithMatch.map(i => i.deal));

  // Saved items hook
  const { isItemSaved, saveItem, unsaveItem } = useSavedItems();

  // The "Rejected" archive is surfaced via an explicit status filter; the
  // default pipeline view hides rejected deals (see marketplace filterFn).
  const showingPassed = filters.status.includes('rejected');

  // The pipeline is placement-derived, not filter-derived: a card is here
  // because its STAGE surfaces on 'dealflow' (screening/interested/diligence),
  // not because a filter chip happens to be off. `invested` lives on
  // 'portfolio' and `monitoring` lives on 'watchlist' — before this, both
  // stayed stuck in the pipeline forever because only 'rejected' was ever
  // excluded (see `matchesDealflowStatusFilter` in lib/state/marketplace.ts,
  // which now uses the same `appearsOn` check for the rendered list).
  // 119-07: deals whose company is SEALED (closed / acquired / bankrupt) —
  // confirmed by the card RPC, never guessed from the NULL profile embed. They
  // stay in the list as a read-only record and leave the pipeline stats: there
  // is no stage left to move them through.
  const sealedDeals = useSealedDeals(dealflow);
  const pipeline = useMemo(
    () =>
      dealflow.filter(
        (d) => appearsOn(d.status, 'dealflow') && !sealedDeals.sealedStartupIds.has(d.startup.id.toLowerCase()),
      ),
    [dealflow, sealedDeals.sealedStartupIds],
  );

  const stats = useMemo(() => ({
    total: pipeline.length,
    screening: pipeline.filter((d) => d.status === 'screening').length,
    interested: pipeline.filter((d) => d.status === 'interested').length,
    // Rejected deals live in the archive, not the pipeline — counted off the
    // full dealflow so the "Rejected N" toggle badge still shows a total.
    passed: dealflow.filter((d) => d.status === 'rejected').length,
    // Average over SCORED deals only. Including unscored ones as 0 would drag
    // the average toward 'critical' purely because scoring had not run.
    // `null` when nothing is scored — the UI shows a dash, not a fake 0.
    avgScore: (() => {
      const scored = pipeline.filter((d) => d.readinessScore != null);
      if (scored.length === 0) return null;
      return Math.round(
        scored.reduce((sum, d) => sum + (d.readinessScore as number), 0) / scored.length,
      );
    })(),
  }), [pipeline, dealflow]);

  const handleViewStartup = (dealId: string) => {
    if (isSelectionMode) {
      toggleSelection(dealId);
    } else {
      router.push(`/startup-detail?id=${dealId}`);
    }
  };

  const handleCompare = () => {
    if (comparisonIds.length >= 2) {
      router.push(`/deal-comparison?ids=${comparisonIds.join(',')}`);
    }
  };

  /**
   * Bulk move. Each selected deal is looked up so the move starts from ITS own
   * stage — a bulk selection spans stages, and `from` is the rollback target if
   * the write is rejected, so a shared assumption would put a rolled-back card
   * somewhere it has never been.
   *
   * A selection also spans stages the move is ILLEGAL from (a terminal deal
   * caught by "select all" while the Rejected filter is on). Those stay put,
   * and the analytics event is gated on the move actually being applied rather
   * than fired per selected id — otherwise the bulk bar reports moves that
   * never happened.
   */
  const bulkChangeStage = useCallback(
    (to: DealStage) => {
      selectedIds.forEach(id => {
        const deal = dealflow.find((d) => d.id === id);
        if (!deal) return;
        if (!changeStage(id, deal.status, to)) return;
        track('dealflow_status_changed', { startupId: id, newStatus: to });
      });
      exitSelectionMode();
    },
    [selectedIds, dealflow, changeStage, exitSelectionMode],
  );

  const handleBulkPass = () => {
    hapticMedium();
    bulkChangeStage('rejected');
  };

  const handleBulkInterested = () => {
    hapticSuccess();
    bulkChangeStage('interested');
  };

  const hasDetailedScoreAccess = canSeePlatformDealflow;

  const handleViewScore = useCallback((dealId: string, e?: { stopPropagation: () => void }) => {
    e?.stopPropagation();
    // Without commerce there is no plans screen to send a locked user to; the
    // score screen itself explains what is locked.
    if (hasDetailedScoreAccess || !openPlans) {
      router.push(`/(investor)/investor-score-detail?dealId=${dealId}`);
    } else {
      openPlans(router, 'investor');
    }
  }, [hasDetailedScoreAccess, router]);

  // Free tier: render the teaser (inbox + upgrade) instead of the pipeline.
  // Investors keep the "Analyze New Startup" inbox action (free basic scoring);
  // the curated platform pipeline is gated behind a paid plan.

  return (
    <AppLayout title="Dealflow" showSearch={false}>
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* Pinned, and carrying the global controls — see the note on score.tsx.
           Discover stays this page's contextual action. No bottomEdge: tab screen. */}
        <ScreenFrame
          title="Dealflow"
          actions={
            <>
              <AnimatedPressable
                onPress={() => router.push('/discover-startups')}
                accessibilityRole="button"
                accessibilityLabel="Discover startups"
                style={{
                  width: 44, height: 44, borderRadius: 22,
                  alignItems: 'center', justifyContent: 'center',
                  backgroundColor: colors.surface.primary,
                }}
              >
                <Compass size={20} color={colors.primary[600]} />
              </AnimatedPressable>
              <ScreenFrame.GlobalActions />
            </>
          }
        >
          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>

            {/* Search Bar */}
            <Animated.View entering={FadeInUp.delay(150).duration(500)} className="px-5 mb-4">
              <SearchBar
                value={filters.query}
                onChangeText={(text) => updateFilter('query', text)}
                placeholder="Search startups..."
                onFilterPress={() => setShowFilters(true)}
                activeFilterCount={activeFilterCount}
              />
            </Animated.View>

            {/* Quick Actions Row */}
            <Animated.View entering={FadeInUp.delay(175).duration(500)} className="px-5 mb-4">
              <View className="flex-row items-center justify-between">
                <View className="flex-row items-center gap-2">
                  <SortDropdown value={sortBy} onChange={setSortBy} />
                  {isDesktop && (
                    <>
                      <AnimatedPressable
                        onPress={() => router.push('/discover-startups')}
                        accessibilityRole="button"
                        accessibilityLabel="Discover startups"
                        className="flex-row items-center gap-2 px-3 py-2 rounded-lg"
                        style={{ backgroundColor: colors.primary[50], borderWidth: 1, borderColor: colors.primary[200] }}
                      >
                        <Compass size={16} color={colors.primary[600]} />
                        <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">Discover</Text>
                      </AnimatedPressable>
                      <AnimatedPressable
                        onPress={() => router.push('/company-directory')}
                        accessibilityRole="button"
                        accessibilityLabel="Browse startup directory"
                        className="flex-row items-center gap-2 px-3 py-2 rounded-lg"
                        style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                      >
                        <MapPin size={16} color={colors.text.secondary} />
                        <Text style={{ color: colors.text.secondary }} className="text-sm font-medium">Startup Directory</Text>
                      </AnimatedPressable>
                    </>
                  )}
                </View>
                <View className="flex-row items-center gap-2">
                  {!isSelectionMode ? (
                    <AnimatedPressable
                      onPress={enterSelectionMode}
                      accessibilityRole="button"
                      accessibilityLabel="Enter selection mode"
                      className="px-3 py-2 rounded-lg"
                      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                    >
                      <Text style={{ color: colors.text.secondary }} className="text-sm">Select</Text>
                    </AnimatedPressable>
                  ) : (
                    <>
                      <AnimatedPressable onPress={selectAll} accessibilityRole="button" accessibilityLabel="Select all deals" className="px-3 py-2 rounded-lg" style={{ backgroundColor: colors.surface.primary }}>
                        <Text style={{ color: colors.text.secondary }} className="text-sm">All</Text>
                      </AnimatedPressable>
                      <AnimatedPressable onPress={exitSelectionMode} accessibilityRole="button" accessibilityLabel="Cancel selection" className="px-3 py-2 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                        <Text style={{ color: colors.text.secondary }} className="text-sm">Cancel</Text>
                      </AnimatedPressable>
                    </>
                  )}
                </View>
              </View>
            </Animated.View>

            {/* Filter Chips */}
            <View className="px-5">
              <FilterChips filters={filters} onToggle={toggleArrayFilter} onReset={resetFilters} />
            </View>

            {/* Section Divider - Overview */}
            <View className="px-5">
              <SectionDivider label="Overview" marginTop={16} marginBottom={12} />
            </View>

            {/* Stats Row */}
            <Animated.View entering={FadeInUp.delay(200).duration(500)} className={`px-5 mb-4 ${isDesktop ? 'pt-2' : ''}`}>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
                <View className="flex-row gap-3">
                  {!hasHydrated ? (
                    <><StatsSkeletonCard /><StatsSkeletonCard /><StatsSkeletonCard /><StatsSkeletonCard /></>
                  ) : (
                    <>
                      <Card elevated borderWidth={0} className="rounded-2xl w-28" accessibilityLabel={`Total Deals: ${stats.total}`}>
                        <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">{stats.total}</Text>
                        <Text style={{ color: colors.text.secondary }} className="text-xs">Total Deals</Text>
                      </Card>
                      <Card backgroundColor={colors.primary[50]} borderColor={colors.primary[100]} className="rounded-2xl w-28" accessibilityLabel={`Screening: ${stats.screening}`}>
                        <Text style={{ color: colors.primary[600] }} className="text-2xl font-bold">{stats.screening}</Text>
                        <Text style={{ color: colors.primary[700] }} className="text-xs">Screening</Text>
                      </Card>
                      <Card backgroundColor={colors.traffic.greenLight} borderColor={colors.traffic.greenMuted} className="rounded-2xl w-28" accessibilityLabel={`Interested: ${stats.interested}`}>
                        <Text style={{ color: colors.success }} className="text-2xl font-bold">{stats.interested}</Text>
                        <Text style={{ color: colors.success }} className="text-xs">Interested</Text>
                      </Card>
                      <Card backgroundColor={colors.primary[50]} borderColor={colors.primary[100]} className="rounded-2xl w-28" accessibilityLabel={stats.avgScore != null ? `Average Score: ${stats.avgScore}` : 'Average score: not available, no deals scored yet'}>
                        <Text style={{ color: colors.primary[600] }} className="text-2xl font-bold">{stats.avgScore ?? '—'}</Text>
                        <Text style={{ color: colors.primary[700] }} className="text-xs">Avg Score</Text>
                      </Card>
                    </>
                  )}
                </View>
              </ScrollView>
            </Animated.View>

            {/* New Analysis CTA */}
            <Animated.View entering={FadeInUp.delay(250).duration(500)} className="px-5 mb-4">
              <AnimatedPressable
                onPress={() => openNewAnalysis()}
                accessibilityRole="button"
                accessibilityLabel="Submit a pitch deck for AI-powered analysis"
                className="rounded-2xl p-4 flex-row items-center justify-between"
                style={{ backgroundColor: colors.primary[50], borderWidth: 1, borderColor: colors.primary[200], borderStyle: 'dashed' }}
              >
                <View className="flex-row items-center gap-3 flex-1">
                  <View className="w-12 h-12 rounded-xl items-center justify-center" style={{ backgroundColor: colors.primary[100] }}>
                    <Sparkles size={24} color={colors.primary[600]} />
                  </View>
                  <View className="flex-1">
                    <Text style={{ color: colors.primary[700] }} className="font-semibold">Analyze New Startup</Text>
                    <Text style={{ color: colors.primary[600] }} className="text-sm">Submit a pitch deck for AI-powered analysis</Text>
                  </View>
                </View>
                <View className="px-3 py-2 rounded-lg flex-row items-center gap-1" style={{ backgroundColor: colors.primary[600] }}>
                  <Plus size={16} color="white" />
                  <Text className="text-white font-medium text-sm">Submit</Text>
                </View>
              </AnimatedPressable>
            </Animated.View>

            {/* Decks still being analysed — and any that failed. These are not
                dealflow rows: `dealflow.startup_id` is NOT NULL and the company
                identity only exists once extraction has run. */}
            <DeckAnalysisCards analyses={deckAnalyses ?? []} onRetry={() => openNewAnalysis()} />

            {/* Section Divider - Startups */}
            <View className="px-5">
              <SectionDivider label="Startups" marginTop={8} marginBottom={12} />
            </View>

            {/* Dealflow List */}
            <Animated.View entering={FadeInUp.delay(300).duration(500)} className="px-5 pb-8">
              <SectionHeader
                title={filters.query ? 'Search Results' : showingPassed ? 'Rejected' : 'Pipeline'}
                className="mb-4"
                right={
                  <View className="flex-row items-center gap-2">
                    <AnimatedPressable
                      onPress={() => {
                        hapticLight();
                        updateFilter('status', showingPassed ? [] : ['rejected']);
                      }}
                      accessibilityRole="switch"
                      accessibilityLabel={showingPassed ? 'Show active pipeline' : 'Show rejected deals'}
                      accessibilityState={{ checked: showingPassed }}
                      className="px-2.5 py-2 rounded-lg active:opacity-70"
                      style={{
                        backgroundColor: showingPassed ? colors.primary[100] : colors.surface.secondary,
                        borderWidth: 1,
                        borderColor: showingPassed ? colors.primary[300] : colors.border.light,
                      }}
                      hitSlop={8}
                    >
                      <Text
                        numberOfLines={1}
                        style={{ color: showingPassed ? colors.primary[700] : colors.text.tertiary, fontSize: 12, fontWeight: '600' }}
                      >
                        {`Rejected${stats.passed ? ` ${stats.passed}` : ''}`}
                      </Text>
                    </AnimatedPressable>
                    <AnimatedPressable
                      onPress={() => { hapticLight(); setIsMasked(!isMasked); }}
                      accessibilityRole="switch"
                      accessibilityLabel={isMasked ? 'Show financial values' : 'Hide financial values'}
                      accessibilityState={{ checked: !isMasked }}
                      className="p-2 rounded-lg active:opacity-70"
                      style={{
                        backgroundColor: colors.surface.secondary,
                        borderWidth: 1,
                        borderColor: isMasked ? colors.border.light : colors.primary[200],
                      }}
                      hitSlop={8}
                    >
                      {isMasked ? (
                        <EyeOff size={16} color={colors.text.tertiary} />
                      ) : (
                        <Eye size={16} color={colors.primary[600]} />
                      )}
                    </AnimatedPressable>
                    <View className="flex-row items-center gap-0.5 p-1 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                      {LAYOUT_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const isActive = layout === opt.value;
                        return (
                          <AnimatedPressable
                            key={opt.value}
                            onPress={() => setLayout(opt.value)}
                            className="p-2 rounded"
                            style={{ backgroundColor: isActive ? colors.surface.primary : 'transparent' }}
                            accessibilityRole="button"
                            accessibilityLabel={`${opt.label} layout`}
                            accessibilityState={{ selected: isActive }}
                          >
                            <Icon size={18} color={isActive ? colors.primary[600] : colors.text.tertiary} />
                          </AnimatedPressable>
                        );
                      })}
                    </View>
                    <Text style={{ color: colors.text.tertiary }} className="text-sm">{itemsWithMatch.length}</Text>
                  </View>
                }
              />

              <View className={`gap-3 ${layout === 'grid' ? 'flex-row flex-wrap' : ''}`}>
                {/* Skeleton loading */}
                {!hasHydrated && [1, 2, 3, 4, 5].map((i) => (
                  <Animated.View key={`skeleton-${i}`} entering={FadeIn.delay(50 + i * 30).duration(200)}>
                    <DealflowSkeletonCard layout={layout} />
                  </Animated.View>
                ))}

                {/* Dealflow items */}
                {hasHydrated && itemsWithMatch.map(({ deal, matchScore }, index) => (
                  <SealedDealSlot
                    key={deal.id}
                    dealId={deal.id}
                    state={sealedDeals.stateFor(deal)}
                    layout={layout}
                    isDesktop={isDesktop}
                    onRetry={sealedDeals.retry}
                    onView={handleViewStartup}
                  >
                  <DealflowCard
                    key={deal.id}
                    deal={deal}
                    index={index}
                    layout={layout}
                    matchScore={matchScore}
                    statusConfig={statusConfig}
                    isExpanded={expandedIds.has(deal.id)}
                    onToggleExpanded={toggleExpanded}
                    isSelectionMode={isSelectionMode}
                    isSelected={isSelected(deal.id)}
                    onToggleSelection={toggleSelection}
                    onEnterSelectionMode={enterSelectionMode}
                    isInComparison={isInComparison(deal.id)}
                    canAddMore={canAddMore}
                    onAddToComparison={addToComparison}
                    onRemoveFromComparison={removeFromComparison}
                    isSaved={isItemSaved(deal.id)}
                    onSave={saveItem}
                    onUnsave={unsaveItem}
                    onViewStartup={handleViewStartup}
                    onViewScore={handleViewScore}
                    onUpdateStatus={changeStage}
                    onTrack={track}
                    requestState={requestStateFor(deal.startup.id, myAccessRequests)}
                    onDDAction={handleDDAction}
                    onRequestAccess={handleRequestAccess}
                    isRequestingAccess={requestingDealId === deal.id}
                    myCommitment={getMyCommitment(deal.startup.id)}
                    isMasked={isMasked}
                  />
                  </SealedDealSlot>
                ))}

                {/* Empty states */}
                {hasHydrated && dealflow.length === 0 && (
                  <DealflowEmptyState
                    isFilterEmpty={false}
                    totalDeals={0}
                    hasQuery={!!filters.query}
                    activeFilterCount={activeFilterCount}
                    onOpenAnalysis={() => openNewAnalysis()}
                    onClearSearch={() => updateFilter('query', '')}
                    onResetFilters={resetFilters}
                    onDiscoverStartups={() => router.push('/discover-startups')}
                    onBrowseDirectory={() => router.push('/company-directory')}
                  />
                )}

                {hasHydrated && dealflow.length > 0 && itemsWithMatch.length === 0 && (
                  <DealflowEmptyState
                    isFilterEmpty={true}
                    totalDeals={dealflow.length}
                    hasQuery={!!filters.query}
                    activeFilterCount={activeFilterCount}
                    onOpenAnalysis={() => openNewAnalysis()}
                    onClearSearch={() => updateFilter('query', '')}
                    onResetFilters={resetFilters}
                    onDiscoverStartups={() => router.push('/discover-startups')}
                    onBrowseDirectory={() => router.push('/company-directory')}
                  />
                )}
              </View>
            </Animated.View>

            {/* Raising Now — platform feed of actively-raising startups.
                Rendered below the pipeline AND when the pipeline is empty, so
                a fresh investor's default dealflow is never a blank screen.

                THIS is the `platform_dealflow` entitlement — a curated feed of
                startups the investor did NOT source. Their OWN pipeline above
                (analysed decks, inbox shortlists) is not a platform feature and
                is never gated.

                Until 2026-08-01 the gate wrapped the WHOLE SCREEN in a separate
                early-return body, so a free investor could analyse a deck (50
                scorings/month, deliberately free) and never see the result. The
                divergent second body also meant anything added to this screen
                silently did not exist for free investors — it happened twice in
                one day. One body, one gated section. */}
            {!canSeePlatformDealflow && (
              <Animated.View entering={FadeInUp.delay(350).duration(500)} className="px-5 pb-8">
                <PlatformDealflowLocked variant="section" />
              </Animated.View>
            )}

            {hasHydrated && canSeePlatformDealflow && raisingItems.length > 0 && (
              <>
                <View className="px-5">
                  <SectionDivider label="Raising Now" marginTop={0} marginBottom={12} />
                </View>
                <Animated.View entering={FadeInUp.delay(350).duration(500)} className="px-5 pb-8">
                  <SectionHeader
                    title="Currently Raising"
                    className="mb-4"
                    right={
                      <Text style={{ color: colors.text.tertiary }} className="text-sm">
                        {raisingItems.length}
                      </Text>
                    }
                  />
                  <View className={`gap-3 ${layout === 'grid' ? 'flex-row flex-wrap' : ''}`}>
                    {raisingItems.map(({ item, matchScore }, index) => (
                      <DealflowCard
                        key={item.id}
                        deal={item}
                        index={index}
                        layout={layout}
                        matchScore={matchScore}
                        statusConfig={raisingStatusConfig}
                        isExpanded={expandedIds.has(item.id)}
                        onToggleExpanded={toggleExpanded}
                        isSelectionMode={false}
                        isSelected={false}
                        onToggleSelection={noop}
                        onEnterSelectionMode={noop}
                        isInComparison={isInComparison(item.id)}
                        canAddMore={canAddMore}
                        onAddToComparison={addToComparison}
                        onRemoveFromComparison={removeFromComparison}
                        isSaved={isItemSaved(item.id)}
                        onSave={saveItem}
                        onUnsave={unsaveItem}
                        onViewStartup={handleViewRaisingStartup}
                        onViewScore={handleViewScore}
                        onUpdateStatus={handleRaisingStatus}
                        onTrack={track}
                        // Raising items have no dealflow row yet, so the card
                        // hides the DD action entirely (source === 'raising').
                        onDDAction={handleDDAction}
                        // Inert here: a raising item is at `screening`, which
                        // does not offer `request-access` (ACTIONS in stage.ts).
                        onRequestAccess={handleRequestAccess}
                        isMasked={isMasked}
                      />
                    ))}
                  </View>
                </Animated.View>
              </>
            )}

            <View className="h-24" />
          </ScrollView>

          {/* Floating bars */}
          {comparisonCount > 0 && !isSelectionMode && (
            <ComparisonBar comparisonCount={comparisonCount} onCompare={handleCompare} onClear={clearComparison} />
          )}
          {isSelectionMode && selectedCount > 0 && (
            <BulkActionsBar selectedCount={selectedCount} onBulkInterested={handleBulkInterested} onBulkPass={handleBulkPass} />
          )}

          <AILauncherFAB targetRoute="/(investor)/assistant" flagKey="ai_assistant" />
        </ScreenFrame>
      </View>

      {/* Filter Panel */}
      <FilterPanel
        visible={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onUpdateFilter={updateFilter}
        onToggleFilter={toggleArrayFilter}
        onReset={resetFilters}
      />

      {/* Pitch Deck Analysis Modal */}
      <PitchDeckAnalysisModal
        visible={showNewAnalysis}
        onClose={() => setShowNewAnalysis(false)}
        onConsentRequired={handleAnalysisConsentRequired}
      />

      {analysisConsentGate.sheet}
    </AppLayout>
  );
}
